import React, { useState, useEffect, useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { ShowCard, CompleteSideCard } from 'src/components/uiElements/cards/ShowCard'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'
import { hepstarAncillaryService } from 'src/services/reservation/item/hepstarAncillary.service'
import { useQuery } from 'react-query'
import { Box, Grid, Typography } from '@mui/material'
import { CounterCard } from 'src/components/pages/dashboard/logic/CounterCard'
import AirStarlingsTicketsList from './AirStarlingsTicketsList'
import { PERMISSION_LIST_SHOW } from 'src/helper/Permission'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import RefreshIcon from '@mui/icons-material/Refresh';
import PieChart from './logic/PieChart'
import dayjs from 'dayjs'

export const DashboardOperations = () => {
  const translate = useTranslate()
  const [count, setCount] = useState(0)
  const [triggerLoadingStarling, setTriggerLoadingStarling] = useState(false)

  const maxCount = 999

  const previousWeekDay = dayjs()
    .subtract(7, 'day')
    .hour(0)
    .minute(0)
    .format('YYYY-MM-DDTHH:mm')

  const emitedWithoutFee = useQuery('emitedWithoutFee', () =>
    reservationServices.getByReservationByPaymentStatus('PARTIALLY_PAID', previousWeekDay)
  )

  const failedEmit = useQuery('failedEmit', () =>
    reservationServices.getByItemByStatus('PENDING,REJECTED,ERROR,UNDEFINED', previousWeekDay)
  )

  const pendingPayment = useQuery('pendingPayment', () =>
    reservationServices.getByReservationByPaymentStatus('UNPAID', previousWeekDay)
  )

  const pendingBilling = useQuery('pendingBilling', () =>
    reservationServices.getByReservationByStatus('FINALIZED', previousWeekDay)
  )

  const pendingHepstar = useQuery('pendingHepstar', () =>
    hepstarAncillaryService.getCountByStatus('PENDING')
  )

  const confirmedReservationsToday = useQuery('confirmedReservationsToday', () =>
    reservationServices.getByReservationByStatusToday('INVOICED', 'PAID,PARTIALLY_PAID,GUARANTEED')
  )

  const operationsStats = useQuery('operationsStats', () =>
    reservationServices.getOperationsStats()
  )

  const incrementCount = useCallback(() => {
    setCount((prevCount) => {
      if (prevCount < maxCount) {
        return prevCount + 1
      } else {
        return 0
      }
    })
  }, [maxCount])

  useEffect(() => {
    let timer
    if (emitedWithoutFee.isLoading
      || failedEmit.isLoading
      || pendingPayment.isLoading
      || pendingBilling.isLoading
      || pendingHepstar.isLoading
    ) {
      timer = setInterval(incrementCount, 100)
    }

    return () => {
      clearInterval(timer)
    }
  }, [incrementCount, emitedWithoutFee.isLoading, failedEmit.isLoading, pendingPayment.isLoading, pendingBilling.isLoading, pendingHepstar.isLoading])

  const getDateTimeFrom = () => (
    dayjs().format('YYYY-MM-DD') + 'T00%3A00'
  )

  const getDateTimeTo = () => (
    dayjs().format('YYYY-MM-DD') + 'T23%3A59'
  )

  return (
    <Box style={{ paddingRight: '100px', paddingLeft: '100px' }}>
      <Grid container spacing={1} columns={20}>
        <Grid item xs={20}>
          <Typography variant='h5'>
            {translate('ra.title.operations_required_action')}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.emited_without_fee')}>
            <CounterCard
              count={emitedWithoutFee.isLoading ? count : emitedWithoutFee.data}
              isLoading={emitedWithoutFee.isLoading}
              border={'20px solid #A9DC62'}
              redirectTo={
                `reservation?displayedFilters=%7B"item__payment_status"%3Atrue%2C"create_at"%3Atrue%7D&filter=%7B"item__payment_status__in"%3A%5B"PARTIALLY_PAID"%5D%2C"create_at__gte"%3A"${previousWeekDay}"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.failed_emit')}>
            <CounterCard
              count={failedEmit.isLoading ? count : failedEmit.data}
              isLoading={failedEmit.isLoading}
              border={'20px solid #FF887F'}
              redirectTo={
                `reservation?displayedFilters=%7B"item__status__in"%3Atrue%2C"create_at"%3Atrue%7D&filter=%7B"item__status__in"%3A%5B"REJECTED"%2C"PENDING"%2C"ERROR"%2C"UNDEFINED"%5D%2C"create_at__gte"%3A"${previousWeekDay}"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.pending_payment_cash')}>
            <CounterCard
              count={pendingPayment.isLoading ? count : pendingPayment.data}
              isLoading={pendingPayment.isLoading}
              border={'20px solid #78DCDC'}
              redirectTo={
                `reservation?displayedFilters=%7B"item__payment_status"%3Atrue%2C"create_at"%3Atrue%7D&filter=%7B"item__payment_status__in"%3A%5B"UNPAID"%5D%2C"create_at__gte"%3A"${previousWeekDay}"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.pending_billing')}>
            <CounterCard
              count={pendingBilling.isLoading ? count : pendingBilling.data}
              isLoading={pendingBilling.isLoading}
              border={'20px solid #FFD862'}
              redirectTo={
                `reservation?displayedFilters=%7B"status__in"%3Atrue%2C"create_at"%3Atrue%7D&filter=%7B"status__in"%3A%5B"FINALIZED"%5D%2C"create_at__gte"%3A"${previousWeekDay}"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.pending_hepstar')}>
            <CounterCard
              count={pendingHepstar.isLoading ? count : pendingHepstar.data}
              isLoading={pendingHepstar.isLoading}
              border={'20px solid #99A5FF'}
              redirectTo={
                'reservation?displayedFilters=%7B"item__has_ancillary_hepstar"%3Atrue%2C"item_ancillary_status"%3Atrue%7D&filter=%7B"item__has_ancillary_hepstar"%3Atrue%2C"item_ancillary_status"%3A"PENDING"%7D&order=DESC&page=1&perPage=10&sort=id'
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={20}>
          <Typography variant='h5'>
            {translate('ra.title.transactions_resume')}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.in_site')}>
            <CounterCard
              count={operationsStats.isLoading ? count : operationsStats.data?.ON_SITE || 0}
              isLoading={operationsStats.isLoading}
              border={'20px solid #78DCDC'}
              redirectTo={
                `operation?displayedFilters=%7B"created_at"%3Atrue%2C"general_status"%3Atrue%7D&filter=%7B"datetime_from__gte"%3A"${getDateTimeFrom()}"%2C"datetime_to__lte"%3A"${getDateTimeTo()}"%2C"general_status"%3A%5B"ON_SITE"%5D%7D&order=DESC&page=1&perPage=10&sort=created_at`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.reserved')}>
            <CounterCard
              count={operationsStats.isLoading ? count : operationsStats.data?.RESERVED || 0}
              isLoading={operationsStats.isLoading}
              border={'20px solid #99A5FF'}
              redirectTo={
                `operation?displayedFilters=%7B"created_at"%3Atrue%2C"general_status"%3Atrue%7D&filter=%7B"datetime_from__gte"%3A"${getDateTimeFrom()}"%2C"datetime_to__lte"%3A"${getDateTimeTo()}"%2C"general_status"%3A%5B"RESERVED"%5D%7D&order=DESC&page=1&perPage=10&sort=created_at`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.cancelled')}>
            <CounterCard
              count={operationsStats.isLoading ? count : operationsStats.data?.CANCELLED || 0}
              isLoading={operationsStats.isLoading}
              border={'20px solid #FF887F'}
              redirectTo={
                `operation?displayedFilters=%7B"created_at"%3Atrue%2C"general_status"%3Atrue%7D&filter=%7B"datetime_from__gte"%3A"${getDateTimeFrom()}"%2C"datetime_to__lte"%3A"${getDateTimeTo()}"%2C"general_status"%3A%5B"CANCELLED"%5D%7D&order=DESC&page=1&perPage=10&sort=created_at`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.error')}>
            <CounterCard
              count={operationsStats.isLoading ? count : operationsStats.data?.ERROR || 0}
              isLoading={operationsStats.isLoading}
              border={'20px solid #FFD862'}
              redirectTo={
                `operation?displayedFilters=%7B"created_at"%3Atrue%2C"general_status"%3Atrue%7D&filter=%7B"datetime_from__gte"%3A"${getDateTimeFrom()}"%2C"datetime_to__lte"%3A"${getDateTimeTo()}"%2C"general_status"%3A%5B"ERROR"%5D%7D&order=DESC&page=1&perPage=10&sort=created_at`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={4}>
          <ShowCard title={translate('ra.title.issued')}>
            <CounterCard
              count={confirmedReservationsToday.data === undefined ? count : confirmedReservationsToday.data}
              isLoading={confirmedReservationsToday.isLoading}
              border={'20px solid #A9DC62'}
              redirectTo={
                `reservation?displayedFilters=%7B"item__status__in"%3Atrue%2C"item__payment_status"%3Atrue%2C"create_at"%3Atrue%7D&filter=%7B"item__status__in"%3A%5B"INVOICED"%5D%2C"item__payment_status__in"%3A%5B"PAID"%2C"PARTIALLY_PAID"%2C"GUARANTEED"%5D%2C"create_at__gte"%3A"${getDateTimeFrom()}"%2C"create_at__lte"%3A"${getDateTimeTo()}"%7D&order=DESC&page=1&perPage=10&sort=id`
              }
            />
          </ShowCard>
        </Grid>

        <Grid item xs={10}>
          <ShowCard
            title={translate('ra.title.share_by_status')}
            contentStyle={{ height: 300, padding: 8 }}
          >
            <PieChart
              labels={[
                translate('ra.title.in_site'),
                translate('ra.title.reserved'),
                translate('ra.title.cancelled'),
                translate('ra.title.error')
              ]}
              backgroundColor={[
                '#78DCDC',
                '#99A5FF',
                '#FF887F',
                '#FFD862'
              ]}
              data={[
                operationsStats.data?.ON_SITE || 0,
                operationsStats.data?.RESERVED || 0,
                operationsStats.data?.CANCELLED || 0,
                operationsStats.data?.ERROR || 0
              ]}
            />
          </ShowCard>
        </Grid>

        <Grid item xs={10}>
          <ShowCard
            title={translate('ra.title.share_by_cancelation_type')}
            contentStyle={{ height: 300, padding: 8 }}
          >
            <PieChart
              labels={[
                translate('ra.title.cancelled_by_fraud'),
                translate('ra.title.cancelled_by_failed_payment'),
                translate('ra.title.cancelled_by_failed_booking'),
                translate('ra.title.cancelled_by_expired_session')
              ]}
              backgroundColor={[
                '#78DCDC',
                '#FFD862',
                '#A9DC62',
                '#99A5FF'
              ]}
              data={[
                operationsStats.data?.CANCELLED_FRAUD || 0,
                operationsStats.data?.CANCELLED_PAYMENT_REJECTED || 0,
                operationsStats.data?.CANCELLED_BOOKING_REJECTED || 0,
                operationsStats.data?.CANCELLED_SESSION_EXPIRED || 0
              ]}
            />
          </ShowCard>
        </Grid>

        {hasPermissionFunction('air-import', PERMISSION_LIST_SHOW) && (
          <Grid item xs={20}>
            <ShowCard
              title={translate('ra.title.air_tickets', {name: 'Starlings'})}
              actionButtonJustify='flex-end'
              actionButtonText={translate('ra.action.refresh_data')}
              actionButtonIcon={<RefreshIcon />}
              actionButtonStyle={{ width: 'fit-content' }}
              actionButtonLoading={triggerLoadingStarling}
              onClickButton={() => setTriggerLoadingStarling(true)}
              avoidPermission={true}
            >
              <CompleteSideCard>
                <AirStarlingsTicketsList
                  refreshData={triggerLoadingStarling}
                  handleFinishRefresh={() => setTriggerLoadingStarling(false)}
                />
              </CompleteSideCard>
            </ShowCard>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
